import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const HOLIDAY_METHODS = [
  { value: "full_time_fixed", label: "Full-time fixed annual entitlement" },
  { value: "part_time_pro_rata", label: "Part-time pro-rata (contracted hours)" },
  { value: "part_time_accrual_8pct", label: "Part-time 8% accrual of hours worked" },
  { value: "manual_override", label: "Manual override" },
];

const SICK_POLICIES = [
  { value: "statutory", label: "Statutory (Irish 2026: 5 days @ 70%, €110/day cap)" },
  { value: "company_scheme", label: "Company scheme" },
  { value: "unpaid", label: "Unpaid" },
  { value: "manual", label: "Manual" },
];

const emptyForm = {
  // Personal
  employee_id: "", full_name: "", email: "", phone: "",
  // Employment
  employment_type: "full_time", department: "", role_title: "",
  start_date: "", end_date: "", status: "active",
  // Hours — GBBS Standard Day baseline (07:30-17:30, 120-min break, 6 days/week)
  contracted_weekly_hours: 48, working_days_per_week: 6, standard_daily_hours: 8,
  default_shift_id: "none",
  default_time_in: "", default_time_out: "", default_break_minutes: 0,
  // Leave
  holiday_method: "full_time_fixed", annual_holiday_entitlement_days: "",
  day_off_entitlement_days: "",
  sick_leave_entitlement_days: 5, sick_leave_policy: "statutory",
  // Payroll-sensitive
  pps_number: "", pay_type: "", hourly_rate: "", annual_salary: "",
};

export default function EmployeesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("all");
  const [status, setStatus] = useState("all");
  const [emptype, setEmptype] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [setupIncomplete, setSetupIncomplete] = useState(0);
  const [shiftPresets, setShiftPresets] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const canManage = ["super_admin", "admin", "hr_user"].includes(user?.role);
  const canSeePay = ["super_admin", "admin", "hr_user"].includes(user?.role);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (dept !== "all") params.department = dept;
    if (status !== "all") params.status = status;
    if (emptype !== "all") params.employment_type = emptype;
    try {
      const { data } = await api.get("/employees", { params });
      setItems(data);
      const d = await api.get("/employees/meta/departments");
      setDepartments(d.data);
      if (canManage) {
        try {
          const c = await api.get("/employees/meta/setup-incomplete-count");
          setSetupIncomplete(c.data?.count || 0);
        } catch { /* ignore */ }
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    api.get("/employees/meta/shift-presets")
      .then((r) => setShiftPresets(r.data || []))
      .catch(() => setShiftPresets([]));
  }, []);

  const calcWeeklyMismatch = () => {
    const dh = Number(form.standard_daily_hours || 0);
    const dw = Number(form.working_days_per_week || 0);
    const cw = Number(form.contracted_weekly_hours || 0);
    if (dh > 0 && dw > 0 && cw > 0 && Math.abs(dh * dw - cw) > 0.01) {
      return `${dh}h/day × ${dw} days = ${(dh * dw).toFixed(1)}h/week, but contracted = ${cw}h/week. Review.`;
    }
    return null;
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, dept, status, emptype]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      ...emptyForm, ...emp,
      start_date: emp.start_date || "", end_date: emp.end_date || "",
      annual_holiday_entitlement_days: emp.annual_holiday_entitlement_days ?? "",
      day_off_entitlement_days: emp.day_off_entitlement_days ?? "",
      contracted_weekly_hours: emp.contracted_weekly_hours ?? emp.standard_weekly_hours ?? 48,
      working_days_per_week: emp.working_days_per_week ?? 5,
      sick_leave_entitlement_days: emp.sick_leave_entitlement_days ?? 5,
      sick_leave_policy: emp.sick_leave_policy ?? "statutory",
      holiday_method: emp.holiday_method ?? "full_time_fixed",
      default_shift_id: emp.default_shift_id || "none",
      default_time_in: emp.default_time_in || "",
      default_time_out: emp.default_time_out || "",
      default_break_minutes: emp.default_break_minutes ?? 0,
    });
    setOpen(true);
  };

  const validateForm = () => {
    if (!form.employee_id?.trim()) return "Employee ID is required";
    if (!form.full_name?.trim()) return "Full name is required";
    if (!form.employment_type) return "Employment type is required";
    if (!form.contracted_weekly_hours || Number(form.contracted_weekly_hours) <= 0) {
      return "Contracted weekly hours are required (must be > 0)";
    }
    return null;
  };

  const save = async () => {
    const err = validateForm();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const payload = {
        ...form,
        contracted_weekly_hours: Number(form.contracted_weekly_hours),
        working_days_per_week: Number(form.working_days_per_week || 5),
        standard_daily_hours: Number(form.standard_daily_hours || 8),
        sick_leave_entitlement_days: Number(form.sick_leave_entitlement_days || 5),
        default_shift_id: form.default_shift_id === "none" ? null : form.default_shift_id,
        default_time_in: form.default_shift_id === "custom" ? form.default_time_in : null,
        default_time_out: form.default_shift_id === "custom" ? form.default_time_out : null,
        default_break_minutes:
          form.default_shift_id === "custom" ? Number(form.default_break_minutes || 0) : null,
      };
      if (payload.annual_holiday_entitlement_days === "" || payload.annual_holiday_entitlement_days == null) {
        delete payload.annual_holiday_entitlement_days;
      } else {
        payload.annual_holiday_entitlement_days = Number(payload.annual_holiday_entitlement_days);
      }
      if (payload.day_off_entitlement_days === "" || payload.day_off_entitlement_days == null) {
        payload.day_off_entitlement_days = null;
      } else {
        payload.day_off_entitlement_days = Number(payload.day_off_entitlement_days);
      }
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      if (!payload.email) delete payload.email;
      if (!canSeePay) {
        delete payload.pps_number; delete payload.pay_type; delete payload.hourly_rate; delete payload.annual_salary;
      } else {
        if (payload.hourly_rate === "" || payload.hourly_rate == null) delete payload.hourly_rate;
        else payload.hourly_rate = Number(payload.hourly_rate);
        if (payload.annual_salary === "" || payload.annual_salary == null) delete payload.annual_salary;
        else payload.annual_salary = Number(payload.annual_salary);
        if (!payload.pay_type) delete payload.pay_type;
        if (!payload.pps_number) delete payload.pps_number;
      }
      if (editing) {
        await api.patch(`/employees/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", payload);
        toast.success("Employee added");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const F = (label, hint, child) => (
    <div>
      <Label className="text-xs">{label}</Label>
      {child}
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tiny-label">Team directory</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Employees</h1>
          <p className="text-muted-foreground mt-1">Employment type, contracted hours, leave entitlement & sick leave.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="add-employee-btn"><Plus className="w-4 h-4 mr-1" /> Add employee</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl" data-testid="employee-dialog">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit employee" : "Add employee"}</DialogTitle>
                <DialogDescription>Employment type and contracted weekly hours are required.</DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="personal" className="mt-2">
                <TabsList className="w-full justify-start flex-wrap">
                  <TabsTrigger value="personal" data-testid="emp-tab-personal">Personal</TabsTrigger>
                  <TabsTrigger value="employment" data-testid="emp-tab-employment">Employment</TabsTrigger>
                  <TabsTrigger value="hours" data-testid="emp-tab-hours">Hours</TabsTrigger>
                  <TabsTrigger value="leave" data-testid="emp-tab-leave">Leave</TabsTrigger>
                  {canSeePay && <TabsTrigger value="payroll" data-testid="emp-tab-payroll">Payroll</TabsTrigger>}
                </TabsList>

                <TabsContent value="personal" className="grid sm:grid-cols-2 gap-4 mt-4">
                  {F("Employee ID *", null,
                    <Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="emp-id-input" />)}
                  {F("Full name *", null,
                    <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="emp-name-input" />)}
                  {F("Email", null,
                    <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email-input" />)}
                  {F("Phone", null,
                    <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />)}
                </TabsContent>

                <TabsContent value="employment" className="grid sm:grid-cols-2 gap-4 mt-4">
                  {F("Employment type *", "Required — drives holiday & overtime logic",
                    <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                      <SelectTrigger data-testid="emp-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time">Full time</SelectItem>
                        <SelectItem value="part_time">Part time</SelectItem>
                      </SelectContent>
                    </Select>)}
                  {F("Status", null,
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>)}
                  {F("Department", null,
                    <Input value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} />)}
                  {F("Role / title", null,
                    <Input value={form.role_title || ""} onChange={(e) => setForm({ ...form, role_title: e.target.value })} />)}
                  {F("Start date", null,
                    <Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />)}
                  {F("End date", "Leave blank if employee is active",
                    <Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />)}
                </TabsContent>

                <TabsContent value="hours" className="grid sm:grid-cols-2 gap-4 mt-4">
                  {F("Contracted weekly hours *", "Required — used as expected hours and overtime threshold",
                    <Input type="number" step="0.5" min="0.5" value={form.contracted_weekly_hours}
                      onChange={(e) => setForm({ ...form, contracted_weekly_hours: e.target.value })} data-testid="emp-weekly-hours" />)}
                  {F("Working days per week", "5 = Mon-Fri; 4 days/week = 16 days holiday at 4-week entitlement",
                    <Input type="number" step="0.5" min="0.5" max="7" value={form.working_days_per_week}
                      onChange={(e) => setForm({ ...form, working_days_per_week: e.target.value })} data-testid="emp-days-per-week" />)}
                  {F("Standard daily hours", null,
                    <Input type="number" step="0.5" min="0.5" max="24" value={form.standard_daily_hours}
                      onChange={(e) => setForm({ ...form, standard_daily_hours: e.target.value })} />)}
                  {F("Default shift", "What gets auto-filled when admin marks this person Working on /in-out. Leave as 'No assigned shift' to use the calculated hours below.",
                    <Select value={form.default_shift_id} onValueChange={(v) => setForm({ ...form, default_shift_id: v })}>
                      <SelectTrigger data-testid="emp-default-shift"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No assigned shift (use calculated hours)</SelectItem>
                        {shiftPresets.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.label} ({p.time_in} – {p.time_out}, {p.break_minutes}m break)
                          </SelectItem>
                        ))}
                        <SelectItem value="custom">Custom hours…</SelectItem>
                      </SelectContent>
                    </Select>)}
                  {form.default_shift_id === "custom" && (
                    <>
                      {F("Custom time in", null,
                        <Input type="time" value={form.default_time_in}
                          onChange={(e) => setForm({ ...form, default_time_in: e.target.value })} data-testid="emp-default-time-in" />)}
                      {F("Custom time out", null,
                        <Input type="time" value={form.default_time_out}
                          onChange={(e) => setForm({ ...form, default_time_out: e.target.value })} data-testid="emp-default-time-out" />)}
                      {F("Custom break (minutes)", null,
                        <Input type="number" min="0" max="480" step="5" value={form.default_break_minutes}
                          onChange={(e) => setForm({ ...form, default_break_minutes: e.target.value })} data-testid="emp-default-break" />)}
                    </>
                  )}
                  <div className="sm:col-span-2 text-xs space-y-1">
                    <div className="text-muted-foreground" data-testid="emp-calc-weekly">
                      Calculated weekly total: <b>
                        {form.standard_daily_hours && form.working_days_per_week
                          ? `${(Number(form.standard_daily_hours) * Number(form.working_days_per_week)).toFixed(1)}h`
                          : "—"}
                      </b>
                      {" · Contracted: "}
                      <b>{form.contracted_weekly_hours || "—"}h</b>
                    </div>
                    {calcWeeklyMismatch() && (
                      <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="emp-hours-warning">
                        ⚠ {calcWeeklyMismatch()}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="leave" className="grid sm:grid-cols-2 gap-4 mt-4">
                  {F("Holiday calculation method", "Part-time accrual uses 8% of approved hours worked, capped at 4 working weeks.",
                    <Select value={form.holiday_method} onValueChange={(v) => setForm({ ...form, holiday_method: v })}>
                      <SelectTrigger data-testid="emp-holiday-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {HOLIDAY_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>)}
                  {F("Annual holiday entitlement (days)", "Used for fixed / manual override; leave blank to use defaults / accrual",
                    <Input type="number" step="0.5" value={form.annual_holiday_entitlement_days}
                      onChange={(e) => setForm({ ...form, annual_holiday_entitlement_days: e.target.value })} data-testid="emp-holiday-days" />)}
                  {F("Day Off entitlement (days/year)", "Earned entitlement, separate from annual leave. Blank = company default (20). Accrues monthly.",
                    <Input type="number" step="0.5" value={form.day_off_entitlement_days}
                      onChange={(e) => setForm({ ...form, day_off_entitlement_days: e.target.value })} data-testid="emp-dayoff-days" />)}
                  {F("Sick leave entitlement (days)", "Defaults to 5 (Irish statutory)",
                    <Input type="number" step="1" value={form.sick_leave_entitlement_days}
                      onChange={(e) => setForm({ ...form, sick_leave_entitlement_days: e.target.value })} data-testid="emp-sick-days" />)}
                  {F("Sick leave policy", null,
                    <Select value={form.sick_leave_policy} onValueChange={(v) => setForm({ ...form, sick_leave_policy: v })}>
                      <SelectTrigger data-testid="emp-sick-policy"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SICK_POLICIES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>)}
                </TabsContent>

                {canSeePay && (
                  <TabsContent value="payroll" className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="pay-rate-section">
                    <div className="sm:col-span-2 text-xs text-muted-foreground">HR / Admin only — managers and employees never see these fields.</div>
                    {F("PPS number", null,
                      <Input value={form.pps_number || ""} onChange={(e) => setForm({ ...form, pps_number: e.target.value })} data-testid="emp-pps" />)}
                    {F("Pay type", null,
                      <Select value={form.pay_type || "none"} onValueChange={(v) => setForm({ ...form, pay_type: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="emp-pay-type"><SelectValue placeholder="Not set" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not set</SelectItem>
                          <SelectItem value="hourly">Hourly</SelectItem>
                          <SelectItem value="salary">Salary</SelectItem>
                        </SelectContent>
                      </Select>)}
                    {F("Hourly rate (€)", null,
                      <Input type="number" step="0.01" value={form.hourly_rate ?? ""} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="emp-hourly-rate" />)}
                    {F("Annual salary (€)", null,
                      <Input type="number" step="100" value={form.annual_salary ?? ""} onChange={(e) => setForm({ ...form, annual_salary: e.target.value })} data-testid="emp-annual-salary" />)}
                  </TabsContent>
                )}
              </Tabs>

              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="emp-cancel">Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="emp-save">{saving ? "Saving…" : "Save"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {canManage && setupIncomplete > 0 && (
        <Alert className="border-amber-200 bg-amber-50 text-amber-800" data-testid="emp-setup-banner">
          <AlertTriangle className="w-4 h-4" />
          <AlertTitle>Setup incomplete</AlertTitle>
          <AlertDescription>
            {setupIncomplete} employee{setupIncomplete === 1 ? " needs" : "s need"} their employment type and contracted weekly hours confirmed. Edit each one and save.
          </AlertDescription>
        </Alert>
      )}

      <Card className="border-border shadow-none">
        <CardContent className="p-4 grid sm:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input placeholder="Search name, ID, email" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="emp-search" />
          </div>
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger data-testid="emp-filter-dept"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={emptype} onValueChange={setEmptype}>
            <SelectTrigger data-testid="emp-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="full_time">Full time</SelectItem>
              <SelectItem value="part_time">Part time</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="emp-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="border-border shadow-none">
        <div className="overflow-x-auto app-scroll">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left tiny-label">
                {canManage && (
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      data-testid="emp-select-all"
                      checked={items.length > 0 && selectedIds.size === items.length}
                      onChange={(e) => setSelectedIds(e.target.checked ? new Set(items.map((x) => x.id)) : new Set())}
                    />
                  </th>
                )}
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3 text-right">Weekly hrs</th>
                <th className="px-4 py-3 text-right">Days/wk</th>
                <th className="px-4 py-3">Default shift</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-t border-border hover:bg-muted/30" data-testid={`emp-row-${e.employee_id}`}>
                  {canManage && (
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        data-testid={`emp-select-${e.employee_id}`}
                        checked={selectedIds.has(e.id)}
                        onChange={(ev) => {
                          setSelectedIds((s) => {
                            const next = new Set(s);
                            if (ev.target.checked) next.add(e.id);
                            else next.delete(e.id);
                            return next;
                          });
                        }}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono text-xs">{e.employee_id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-2">
                      {e.full_name}
                      {e.setup_complete === false && (
                        <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 text-[10px]">setup incomplete</Badge>
                      )}
                      {e.weekly_hours_mismatch && (
                        <Badge
                          variant="outline"
                          className="text-orange-700 border-orange-200 bg-orange-50 text-[10px]"
                          title={e.weekly_hours_mismatch.message}
                          data-testid={`emp-mismatch-${e.employee_id}`}
                        >
                          hours mismatch
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{e.email || "—"}</div>
                    {e.weekly_hours_mismatch && (
                      <div className="text-[11px] text-orange-700 mt-1">
                        {e.weekly_hours_mismatch.daily_hours}h × {e.weekly_hours_mismatch.days_per_week} days = {e.weekly_hours_mismatch.computed_weekly}h, contracted {e.weekly_hours_mismatch.contracted_weekly}h
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">{e.department || "—"}</td>
                  <td className="px-4 py-3 capitalize">{(e.employment_type || "").replace("_", " ") || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{e.contracted_weekly_hours ?? e.standard_weekly_hours ?? "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{e.working_days_per_week ?? "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {e.default_shift_id
                      ? (e.default_shift_id === "custom"
                          ? `Custom ${e.default_time_in || "?"}–${e.default_time_out || "?"}`
                          : (shiftPresets.find((p) => p.id === e.default_shift_id)?.label || e.default_shift_id))
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={e.status === "active" ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-muted-foreground"}>
                      {e.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canManage && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(e)} data-testid={`emp-edit-${e.employee_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={canManage ? 10 : 9} className="px-4 py-10 text-center text-muted-foreground">No employees match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Bulk action bar */}
      {canManage && selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white border border-border rounded-full shadow-lg px-4 py-2 flex items-center gap-3" data-testid="emp-bulk-bar">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="emp-bulk-assign-shift">Assign default shift</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} data-testid="emp-bulk-clear">Clear</Button>
        </div>
      )}

      <BulkShiftDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        shiftPresets={shiftPresets}
        selectedIds={Array.from(selectedIds)}
        onDone={async () => {
          setBulkOpen(false);
          setSelectedIds(new Set());
          await load();
        }}
      />
    </div>
  );
}


function BulkShiftDialog({ open, onOpenChange, shiftPresets, selectedIds, onDone }) {
  const [shiftId, setShiftId] = useState("none");
  const [tIn, setTIn] = useState("");
  const [tOut, setTOut] = useState("");
  const [brk, setBrk] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setShiftId("none"); setTIn(""); setTOut(""); setBrk(0); } }, [open]);

  const apply = async () => {
    setBusy(true);
    try {
      const body = { employee_ids: selectedIds, default_shift_id: shiftId };
      if (shiftId === "custom") {
        if (!tIn || !tOut) { setBusy(false); return toast.error("Custom shift requires time in and time out"); }
        body.default_time_in = tIn;
        body.default_time_out = tOut;
        body.default_break_minutes = Number(brk || 0);
      }
      const r = await api.post("/employees/bulk-assign-shift", body);
      toast.success(`Updated ${r.data.updated} employee${r.data.updated === 1 ? "" : "s"}`);
      await onDone();
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="emp-bulk-dialog">
        <DialogHeader>
          <DialogTitle>Assign default shift to {selectedIds.length} employee{selectedIds.length === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>This sets the default shift that auto-fills when admin marks the person Working on the In/Out page. Manually edited records are NOT overwritten.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={shiftId} onValueChange={setShiftId}>
            <SelectTrigger data-testid="emp-bulk-shift-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No assigned shift (clear)</SelectItem>
              {shiftPresets.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.label} ({p.time_in}–{p.time_out}, {p.break_minutes}m break)</SelectItem>
              ))}
              <SelectItem value="custom">Custom hours…</SelectItem>
            </SelectContent>
          </Select>
          {shiftId === "custom" && (
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Time in</Label><Input type="time" value={tIn} onChange={(e) => setTIn(e.target.value)} /></div>
              <div><Label className="text-xs">Time out</Label><Input type="time" value={tOut} onChange={(e) => setTOut(e.target.value)} /></div>
              <div><Label className="text-xs">Break (min)</Label><Input type="number" min="0" step="5" value={brk} onChange={(e) => setBrk(e.target.value)} /></div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={apply} disabled={busy} data-testid="emp-bulk-apply">{busy ? "Applying…" : "Apply"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
